/*
 * Copyright © 2015-2019 Serenova, LLC. All rights reserved.
 */

/**
 *
 * SalesforceForm
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Button, DetailHeader, InputField, ToggleField, DetailsPanelMessage, Toast } from 'cx-ui-components';
import DetailWrapper from '../../../../components/DetailWrapper';
import styled from 'styled-components';


const WrappedDetailHeader = styled(DetailHeader)`
  margin-left: 35px;
  margin-bottom: 0;
`;

const DetailsPanelMessageTight = styled(DetailsPanelMessage)`
  margin-top: 4px;
  margin-bottom: 20px;
`;

const DetailsPanelMessageInline = styled(DetailsPanelMessage)`
  margin-top: -6px !important;
  margin-bottom: 20px;

  p {
    margin-top: 0;
    margin-bottom: 0;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 8px 0 24px 0;
  box-sizing: border-box;
`;

const ButtonWrapperCenter = styled.div`
  display: flex;
  justify-content: center;
  margin: 8px 0 ${props => (props.$tight ? '8px' : '24px')} 0;
  box-sizing: border-box;
`;

export default function SalesforceForm({
  isSaving,
  dirty,
  inherited,
  userHasUpdatePermission,
  integrationActive,
  initialValues,
  salesforceAuthVersion,
  salesforceLoginUrl,
  change,
  onStartSalesforceOauth,
  onSalesforceOauthReturnSuccess,
}) {
  const integrationId = initialValues?.get
    ? initialValues.get('id')
    : initialValues && initialValues.id;
  const isNewIntegration = integrationId === undefined || integrationId === null;

  const rawAuthVersion = salesforceAuthVersion ||
    (initialValues?.getIn
      ? initialValues.getIn(['properties', 'authVersion'])
      : initialValues && initialValues.properties
        ? initialValues.properties.authVersion
        : undefined);

  const authVersion = isNewIntegration
    ? (rawAuthVersion || 'v2-auth-code')
    : (rawAuthVersion || 'v1-password');
  const isV2 = authVersion === 'v2-auth-code';
  const isV1 = !isV2;
  const [isConnecting, setIsConnecting] = React.useState(false);
  const oauthReturnHandledRef = React.useRef(false);

  React.useEffect(() => {
    if (change && salesforceAuthVersion !== authVersion) {
      change('properties.authVersion', authVersion);
    }
  }, [change, salesforceAuthVersion, authVersion]);

  const loginUrl = salesforceLoginUrl !== undefined
    ? salesforceLoginUrl
    : (initialValues?.getIn
      ? initialValues.getIn(['properties', 'loginUrl'])
      : initialValues && initialValues.properties
        ? initialValues.properties.loginUrl
        : '');

  const connectionState = initialValues?.getIn
    ? initialValues.getIn(['properties', 'connectionState'])
    : initialValues && initialValues.properties
      ? initialValues.properties.connectionState
      : '';

  const triggerConnectionRefresh = React.useCallback((resolvedIntegrationId) => {
    if (!resolvedIntegrationId || !onSalesforceOauthReturnSuccess) {
      return;
    }

    onSalesforceOauthReturnSuccess({ integrationId: resolvedIntegrationId });
  }, [onSalesforceOauthReturnSuccess]);

  const handleOauthSuccess = React.useCallback((resolvedIntegrationId, showToast = false) => {
    setIsConnecting(false);

    // Some callback pages post multiple success events; only refresh once per connect attempt.
    if (oauthReturnHandledRef.current) {
      return;
    }

    oauthReturnHandledRef.current = true;
    triggerConnectionRefresh(resolvedIntegrationId);

    if (showToast) {
      Toast.success('Salesforce connection updated.');
    }
  }, [triggerConnectionRefresh]);

  const persistedAuthVersion = initialValues?.getIn
    ? initialValues.getIn(['properties', 'authVersion'])
    : initialValues && initialValues.properties
      ? initialValues.properties.authVersion
      : undefined;

  const hasUnsavedV1ToV2Migration =
    !!integrationId &&
    persistedAuthVersion !== 'v2-auth-code' &&
    authVersion === 'v2-auth-code';

  const requiresSaveBeforeConnect = hasUnsavedV1ToV2Migration;

  const canConnect =
    isV2 &&
    !isSaving &&
    !isConnecting &&
    !inherited &&
    userHasUpdatePermission &&
    !!integrationId &&
    !requiresSaveBeforeConnect;

  React.useEffect(() => {
    const onMessage = (event) => {
      if (!event || !event.data) return;
      const { data } = event;
      if (data.type === 'oauth-return') {
        if (data.status === 'success') {
          handleOauthSuccess(data.integrationId || integrationId, true);
        } else {
          setIsConnecting(false);
          Toast.error(data.detail || 'Salesforce OAuth callback failed.');
        }
      }
    };

    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [integrationId, handleOauthSuccess]);

  // Clear isConnecting when the connection state updates in Redux after OAuth refresh
  React.useEffect(() => {
    if (isConnecting && oauthReturnHandledRef.current) {
      // Redux has updated with new data; clear the connecting state
      setIsConnecting(false);
    }
  }, [connectionState, isConnecting]);

  const handleConnectToSalesforce = (event) => {
    if (event && typeof event.preventDefault === 'function') {
      event.preventDefault();
    }
    if (event && typeof event.stopPropagation === 'function') {
      event.stopPropagation();
    }
    if (!integrationId) {
      Toast.error('Save the integration before connecting to Salesforce.');
      return;
    }
    if (onStartSalesforceOauth) {
      oauthReturnHandledRef.current = false;
      setIsConnecting(true);
      onStartSalesforceOauth({ integrationId, loginUrl });
    }
  };

  const connectButtonText = 'Connect to Salesforce';

  const handleMigrateToOAuth = (event) => {
    if (event && typeof event.preventDefault === 'function') {
      event.preventDefault();
    }
    if (event && typeof event.stopPropagation === 'function') {
      event.stopPropagation();
    }
    if (change) {
      change('properties.authVersion', 'v2-auth-code');
    }
  };

  return (
    <DetailWrapper open data-automation="salesforcePropertiesSVG">
      <WrappedDetailHeader text="Properties" />
      {isV1 && (
        <>
          <DetailsPanelMessageTight
            text="This integration uses the legacy username/password flow."
            type="warning"
          />
          <ButtonWrapper>
            <Button
              type="button"
              buttonType="primary"
              onClick={handleMigrateToOAuth}
              disabled={isSaving || inherited || !userHasUpdatePermission || integrationActive}
              data-automation="migrateToOAuthBtn"
            >
              Migrate to OAuth
            </Button>
          </ButtonWrapper>
          <InputField
            name="properties.username"
            label="Username *"
            componentType="input"
            inputType="text"
            data-automation="integrationsUsername"
            disabled={isSaving || inherited || !userHasUpdatePermission}
          />
          <InputField
            name="properties.password"
            label="Password *"
            componentType="input"
            inputType="text"
            data-automation="integrationsPassword"
            disabled={isSaving || inherited || !userHasUpdatePermission}
            type="password"
          />
          <InputField
            name="properties.consumerKey"
            label="Consumer Key *"
            componentType="input"
            inputType="text"
            data-automation="integrationsConsumerKey"
            disabled={isSaving || inherited || !userHasUpdatePermission}
          />
          <InputField
            name="properties.consumerSecret"
            label="Consumer Secret *"
            componentType="input"
            inputType="text"
            data-automation="integrationsConsumerSecret"
            disabled={isSaving || inherited || !userHasUpdatePermission}
            type="password"
            autoComplete={'new-password'}
          />
          <InputField
            name="properties.securityToken"
            label="Security Token *"
            componentType="input"
            inputType="text"
            data-automation="integrationsSecurityToken"
            disabled={isSaving || inherited || !userHasUpdatePermission}
            type="password"
            autoComplete={'new-password'}
          />
        </>
      )}
      {isV2 && (
        <>
          <ButtonWrapperCenter $tight={requiresSaveBeforeConnect}>
            <Button
              type="button"
              buttonType="primary"
              onClick={handleConnectToSalesforce}
              disabled={!canConnect}
              data-automation="connectToSalesforceBtn"
            >
              {connectButtonText}
            </Button>
          </ButtonWrapperCenter>
          {requiresSaveBeforeConnect && (
            <DetailsPanelMessageInline
              text="To continue, click Submit to save this integration. After saving, Connect to Salesforce will be enabled."
              type="warning"
            />
          )}
        </>
      )}
      <InputField
        name="properties.loginUrl"
        label="Login URL"
        componentType="input"
        inputType="text"
        data-automation="integrationsLoginUrl"
        disabled={isSaving || inherited || !userHasUpdatePermission}
      />
      <ToggleField
        name="properties.pushEnabled"
        label="Activity Records"
        data-automation="integrationsPushEnabled"
        disabled={isSaving || inherited || !userHasUpdatePermission}
      />
      <DetailsPanelMessage
        text={`If Login URL is left blank, you will be defaulted to Salesforce Production URL.`}
        type="warning"
      />
    </DetailWrapper>
  );
}

SalesforceForm.propTypes = {
  isSaving: PropTypes.bool,
  dirty: PropTypes.bool,
  inherited: PropTypes.bool,
  integrationActive: PropTypes.bool,
  userHasUpdatePermission: PropTypes.bool,
  initialValues: PropTypes.object,
  salesforceAuthVersion: PropTypes.string,
  salesforceLoginUrl: PropTypes.string,
  change: PropTypes.func,
  onStartSalesforceOauth: PropTypes.func,
  onSalesforceOauthReturnSuccess: PropTypes.func,
};
